import { Box, Button, Fade, InputAdornment, Link, OutlinedInput, Stack, SxProps } from "@mui/material";

import SelectModeBanner from "../../Banners/SelectModeBanner";
import Panel from "../Panel/panel";
import PanelHeader, { PanelHeaderProps } from "../PanelHeader/panel-header";

import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import {
  getGridStringOperators,
  GridColDef,
  GridFilterInputValue,
  GridFilterModel,
  GridFilterOperator,
  GridRowsProp,
} from "@mui/x-data-grid-premium";
import { FC, MouseEventHandler, useMemo, useState } from "react";
import { ObjectClass } from "../../../Core/Api/Types/object-class";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import {
  FetchFullPlaceholdersByIds,
  GetIsMarketplaceLoading,
  GetNotSelectedPlaceholders,
} from "../../../Redux/Reducers/redux-slice-new-request";
import { useAppDispatch, useAppSelector } from "../../../Redux/redux-store";
import { GetEnumValueLabelMap } from "../../../Utils/helpers";
import AssetClassChip from "../../Misc/Chips/AssetChip/asset-class-chip";
import AssetTypeChip from "../../Misc/Chips/AssetChip/asset-type-chip";
import DataTable, { CellSize } from "../../Table/table";
import TableCellStringExpend from "../../Table/TableCellStringExpend/table-cell-string-expend";
import TableSkeleton from "../../Table/TableSkeleton";
import { DbIngestConfigPackPlaceholderRowInfos } from "../../../Core/Api/Types/db-ingest-config-pack-placeholder-row-infos";
import DrawerWrapper from "../../Drawers/Drawer/drawer-wrapper";
import { useTranslation } from "react-i18next";
import TableUuids from "../../Table/table-uuids";
import { customOperators } from "../../../Utils/Helpers/customGridFilterOperators";

export interface DrawerProps {
  sx?: SxProps;
  width?: number;
  open: boolean;
  onClose?: MouseEventHandler;
  closePanel: () => void;
}

const MarketplacePlaceholdersPanel: FC<DrawerProps> = (props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [selectedRowIds, setSelectedRowIds] = useState<number[]>([]);
  const rows = useAppSelector(GetNotSelectedPlaceholders);

  const isMarketplaceLoading = useAppSelector(GetIsMarketplaceLoading);

  let _rows: GridRowsProp = [];
  type Row = (typeof _rows)[number];
  const columns: GridColDef<DbIngestConfigPackPlaceholderRowInfos>[] = useMemo(
    () => [
      {
        headerName: t("common:assetType"),
        field: "objectType",
        minWidth: CellSize.small,
        flex: CellSize.small,
        type: "singleSelect",
        valueOptions: GetEnumValueLabelMap(ObjectType),
        renderCell: (data: Row) => <AssetTypeChip type={data.value} />,
      },
      {
        headerName: t("table:headerName.assetClass"),
        field: "objectClasses",
        minWidth: CellSize.small,
        flex: CellSize.small,
        type: "singleSelect",
        valueOptions: GetEnumValueLabelMap(ObjectClass),
        valueGetter: (data, row) => row.objectClasses,
        renderCell: (data: Row) => (
          <>
            {data.value.map((item, index) => (
              <AssetClassChip key={index} classification={item} />
            ))}
          </>
        ),
      },
      {
        headerName: t("table:headerName.configurationName"),
        field: "name",
        minWidth: CellSize.large,
        flex: CellSize.large,
        renderCell: (data: Row) => <div style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{data.value}</div>,
      },
      {
        headerName: t("table:headerName.configurationDescription"),
        field: "description",
        minWidth: CellSize.xxlarge,
        flex: CellSize.xxlarge,
        type: "string",
        filterOperators: [customOperators.includesAllWords],
        renderCell: (data: Row) => <TableCellStringExpend data={data} />,
      },
    ],
    []
  );

  return (
    <DrawerWrapper
      open={props.open}
      width={`${(1150 / 1280) * 100}%`}
      onClose={props.onClose}
      anchor="right"
      header={<PanelHeader title={t("common:marketplace")} onClose={props.onClose as PanelHeaderProps["onClose"]} />}
    >
      <Fade in={selectedRowIds.length > 0}>
        <Box sx={{ position: "absolute", zIndex: 2, top: 0, left: 0, right: 0 }}>
          <SelectModeBanner
            text={t("panel:marketplacePlaceholdersPanel.body.placeholdersSelected", { count: selectedRowIds.length })}
            onCloseClick={() => setSelectedRowIds([])}
          >
            <Link underline="hover" color="text.primary" onClick={() => setSelectedRowIds([])}>
              {t("common:unselectAllUpperCase")}
              <CloseIcon sx={{ ml: 0.5 }} />
            </Link>
            <Button
              variant="white"
              size="small"
              startIcon={<AddIcon />}
              sx={{ ml: 1.5 }}
              onClick={() => {
                dispatch(FetchFullPlaceholdersByIds(selectedRowIds));
                props.closePanel();
              }}
            >
              {t("button:addToRequest")}
            </Button>
          </SelectModeBanner>
        </Box>
      </Fade>
      {isMarketplaceLoading ? (
        <>
          <TableSkeleton />
        </>
      ) : (
        <Stack sx={{ flex: "1 1 0", minHeight: 0 }}>
          <DataTable
            tableKey={TableUuids.NEW_REQUEST_PLACEHOLDER_MARKETPLACE}
            columns={columns}
            rows={rows}
            selectedRows={selectedRowIds}
            setSelectedRows={setSelectedRowIds}
            checkboxSelection
            disableRowSelectionOnClick
          />
        </Stack>
      )}
    </DrawerWrapper>
  );
};

export default MarketplacePlaceholdersPanel;
